import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import hero from "../Assets/serhant-start-hero.jpg";
import "./SerhantStart.css";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=GFS+Didot&display=swap";
const DEMO_SRC = `${process.env.PUBLIC_URL || ""}/mf-smple-demo.mp4`;
const UPLOAD_SRC = "/external-ai-upload?lightbox=1";

/**
 * Public Serhant landing page at /start.
 * Mobile: hero image above the headline and CTAs.
 * Desktop: copy on the left, hero image on the right.
 */
const SerhantStart = () => {
  const videoRef = useRef(null);
  const dialogRef = useRef(null);
  const uploadDialogRef = useRef(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Just hit shuffle.";

    const existing = document.querySelector(`link[href="${FONT_HREF}"]`);
    if (!existing) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }

    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    [
      [dialogRef.current, demoOpen],
      [uploadDialogRef.current, uploadOpen],
    ].forEach(([node, open]) => {
      if (!node) return;
      if (open) node.removeAttribute("inert");
      else node.setAttribute("inert", "");
    });

    if (!demoOpen && !uploadOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (uploadOpen) closeUpload();
      else closeDemo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [demoOpen, uploadOpen]);

  function watchDemo() {
    setUploadOpen(false);
    setDemoOpen(true);
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    const pending = video.play();
    if (pending && typeof pending.catch === "function") {
      pending.catch(() => {});
    }
  }

  function closeDemo() {
    const video = videoRef.current;
    if (video) video.pause();
    setDemoOpen(false);
  }

  function openUpload() {
    const video = videoRef.current;
    if (video) video.pause();
    setDemoOpen(false);
    setUploadOpen(true);
  }

  function closeUpload() {
    setUploadOpen(false);
  }

  return (
    <main className="serhant-start">
      <div className="serhant-start-grid">
        <div className="serhant-start-copy">
          <h1>Just hit shuffle.</h1>
          <p className="serhant-start-lead">
            Give people a way to try on your floor plans for size and make more
            confident decisions.
          </p>
          <div className="serhant-start-actions">
            <button type="button" className="serhant-start-primary" onClick={openUpload}>
              Upload floor plan
            </button>
            <button type="button" className="serhant-start-secondary" onClick={watchDemo}>
              Watch demo
            </button>
            <Link className="serhant-start-tertiary" to="/docs">
              Engineering docs
            </Link>
          </div>
        </div>
        <img
          className="serhant-start-hero"
          src={hero}
          alt="SERHANT floor plan monogram"
        />
      </div>

      <div
        ref={dialogRef}
        className={`serhant-start-demo${demoOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Demo"
        aria-hidden={!demoOpen}
      >
        <button
          type="button"
          className="serhant-start-demo-backdrop"
          aria-label="Close demo"
          onClick={closeDemo}
        />
        <div className="serhant-start-demo-frame">
          <video
            ref={videoRef}
            src={DEMO_SRC}
            controls
            playsInline
            preload="metadata"
          />
        </div>
      </div>

      <div
        ref={uploadDialogRef}
        className={`serhant-start-demo${uploadOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Upload floor plan"
        aria-hidden={!uploadOpen}
      >
        <button
          type="button"
          className="serhant-start-demo-backdrop"
          aria-label="Close upload"
          onClick={closeUpload}
        />
        <div className="serhant-start-upload-frame">
          {uploadOpen ? (
            <iframe title="Upload floor plan" src={UPLOAD_SRC} />
          ) : null}
        </div>
      </div>
    </main>
  );
};

export default SerhantStart;
